import { createServerOnlyFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { callTRPCProcedure } from '@trpc/server'
import { appRouter, createContext } from 'server/trpc'
import { contextRequestForDocument } from './trpc-server-fn.helpers.ts'

type TRPCCallInput = { path: string; input: unknown; type: 'query' | 'mutation' }

// Keyed by the per-document Request, so the auth middleware resolves the session and runs getUserFromDB once per
// page instead of once per query; the WeakMap self-evicts, so nothing leaks on the long-lived server.
const documentRequestCaches = new WeakMap<Request, Map<string, Promise<unknown>>>()

function cacheForDocumentRequest(req: Request): Map<string, Promise<unknown>> {
  let cache = documentRequestCaches.get(req)
  if (!cache) {
    cache = new Map()
    documentRequestCaches.set(req, cache)
  }
  return cache
}

/**
 * The SSR tRPC bridge: `ssrLink` (main.tsx) hands every in-process query of a document render here, and it runs
 * the procedure DIRECTLY against `appRouter` — no HTTP, and no server-function RPC either.
 * `createServerOnlyFn` keeps the one thing the server-fn boundary was for: the start compiler rewrites this call
 * in the CLIENT environment into a throwing stub, so dead-code elimination drops the router and its imports from
 * the client bundle. Why the wrapper went, and what it cost: docs/notes/app/trpc-server-fn.md
 */
export const trpcSSRCallFn = createServerOnlyFn(async ({ path, input, type }: TRPCCallInput): Promise<unknown> => {
  const documentRequest = getRequest()
  // The derived context request is memoized per document alongside the memo map above, so a document's
  // ~6 fanned-out queries derive it once instead of once each (see trpc-server-fn.helpers.ts).
  const req = contextRequestForDocument(documentRequest)
  return callTRPCProcedure({
    router: appRouter,
    path,
    getRawInput: () => Promise.resolve(input),
    // Share one request-scoped memo map across every SSR tRPC call of this document (see above).
    ctx: createContext({ req, cache: cacheForDocumentRequest(documentRequest) }),
    type,
    signal: undefined,
    batchIndex: 0,
  })
})
